import { createSelector } from '@reduxjs/toolkit'

import { ApplicationState } from 'redux/Stores/types'
import { TGroup, TGroupApi } from 'types/GlobalGroup'
import {
  TKeyMetric,
  TKeyMetricApi,
  TKeyMetricDataBody,
  TKeyMetricRawData
} from 'types/GlobalKeyMetric'
import { getKeyMetricDataKey } from 'utils/metric'
import { TDashboardGroupResponse } from 'types/GlobalDashboardGroup'
import { TReport } from 'types/GlobalReport'
import { ResourceEntry } from 'redux/reducers/ResourceKeyMetricLinker'

const selectKeyMetricStore = (state: ApplicationState) => state.KeyMetricStore

export const selectIsCreatingKeyMetric = createSelector(
  [selectKeyMetricStore],
  (store) => store.isCreating
)

const buildResourcesMapper = (
  byKeyMetricId: Record<string, ResourceEntry[]>,
  dashboardGroups: Record<string, TDashboardGroupResponse>,
  reports: Record<string, TReport>
): Record<string, (TDashboardGroupResponse | TReport)[]> => {
  const mapper: Record<string, (TDashboardGroupResponse | TReport)[]> = {}

  Object.entries(byKeyMetricId).forEach(([kmId, resourceEntries]) => {
    mapper[kmId] = resourceEntries
      .map(({ id, type }) =>
        type === 'report' ? reports[id] : dashboardGroups[id]
      )
      .filter(Boolean) as (TDashboardGroupResponse | TReport)[]
  })

  return mapper
}

const buildGroupsMapper = (
  linkerByKeyMetricId: Record<string, string[]>,
  groups: Record<string, TGroupApi>
): Record<string, Omit<TGroup, 'key_metrics'>[]> => {
  const mapper: Record<string, Omit<TGroup, 'key_metrics'>[]> = {}

  Object.entries(linkerByKeyMetricId).forEach(([kmId, groupIds]) => {
    mapper[kmId] = groupIds.map((gid) => groups[gid]).filter(Boolean)
  })

  return mapper
}

const processChildren = (
  children: any[],
  groupsMapper: Record<string, TKeyMetric['groups']>,
  resourcesMapper: Record<string, (TDashboardGroupResponse | TReport)[]>,
  parent: TKeyMetric
): TKeyMetric[] => {
  return children.map((child): TKeyMetric => {
    const processedChild: TKeyMetric = {
      ...child,
      children: [],
      groups: groupsMapper[child.id] || [],
      parent: parent,
      resources: resourcesMapper[child.id] || []
    }

    if (child.children && child.children.length > 0) {
      processedChild.children = processChildren(
        child.children,
        groupsMapper,
        resourcesMapper,
        processedChild
      )
    }

    return processedChild
  })
}

const formatKeyMetric = (
  keyMetric: TKeyMetricApi,
  groupsMapper: Record<string, Omit<TGroup, 'key_metrics'>[]>,
  resourcesMapper: Record<string, (TDashboardGroupResponse | TReport)[]>
): TKeyMetric => {
  const rootMetric: TKeyMetric = {
    ...keyMetric,
    children: [],
    groups: groupsMapper[keyMetric.id] || [],
    parent: null,
    pin_comment: keyMetric.pin_comment,
    pin_id: keyMetric.pin_id,
    pinned: keyMetric.pinned,
    resources: resourcesMapper[keyMetric.id] || []
  }

  if (keyMetric.children && keyMetric.children.length > 0) {
    rootMetric.children = processChildren(
      keyMetric.children,
      groupsMapper,
      resourcesMapper,
      rootMetric
    )
  }

  return rootMetric
}

// @TODO: Remove and build store completely by id?
const findKeyMetricById = (
  data: Record<string, TKeyMetricApi>,
  id: string
): TKeyMetricApi | null => {
  const search = (nodes: TKeyMetricApi[]): TKeyMetricApi | null => {
    for (const node of nodes) {
      if (node.id === id) return node

      if (node.children?.length > 0) {
        const found = search(node.children)

        if (found) return found
      }
    }

    return null
  }

  return search(Object.values(data))
}

export const selectKeyMetric = createSelector(
  [
    (state: ApplicationState) => state.KeyMetricStore.data,
    (state: ApplicationState) => state.GroupStore.data,
    (state: ApplicationState) => state.GroupKeyMetricLinkerStore.byKeyMetricId,
    (state: ApplicationState) =>
      state.ResourceKeyMetricLinkerStore.byKeyMetricId,
    (state: ApplicationState) => state.DashboardGroupStore.data,
    (state: ApplicationState) => state.ReportStore.reports,
    (state: ApplicationState, id: string | null) => id
  ],
  (data, groups, linkerByKm, resourcesByKm, dashboardGroups, reports, id) => {
    if (!id) return null

    const keyMetric = findKeyMetricById(data, id)

    if (!keyMetric) return null

    const groupsMapper = buildGroupsMapper(linkerByKm, groups)
    const resourcesMapper = buildResourcesMapper(
      resourcesByKm,
      dashboardGroups,
      reports
    )

    return formatKeyMetric(keyMetric, groupsMapper, resourcesMapper)
  }
)

export const selectKeyMetrics = createSelector(
  [
    (state: ApplicationState) => state.KeyMetricStore.data,
    (state: ApplicationState) => state.KeyMetricStore.fetched,
    (state: ApplicationState) => state.GroupStore.data,
    (state: ApplicationState) => state.GroupKeyMetricLinkerStore.byKeyMetricId,
    (state: ApplicationState) =>
      state.ResourceKeyMetricLinkerStore.byKeyMetricId,
    (state: ApplicationState) => state.DashboardGroupStore.data,
    (state: ApplicationState) => state.ReportStore.reports
  ],
  (
    data,
    fetched,
    groups,
    linkerByKm,
    resourcesByKm,
    dashboardGroups,
    reports
  ): [TKeyMetric[], boolean] => {
    const groupsMapper = buildGroupsMapper(linkerByKm, groups)
    const resourcesMapper = buildResourcesMapper(
      resourcesByKm,
      dashboardGroups,
      reports
    )

    const keyMetrics = Object.values(data)
      .filter((km) => !km.parent)
      .map(
        (km): TKeyMetric => formatKeyMetric(km, groupsMapper, resourcesMapper)
      )
      .sort((a, b) => a.index - b.index)

    return [keyMetrics, fetched]
  }
)

export const selectKeyMetricRawData = createSelector(
  [
    (state: ApplicationState) => state.KeyMetricStore.rawData,
    (_: ApplicationState, id: string) => id,
    (_: ApplicationState, id: string, body?: TKeyMetricDataBody) => body
  ],
  (rawData, id, body): [TKeyMetricRawData | null, number | null] => {
    const dataKey = getKeyMetricDataKey(body)
    const entry = rawData[id]?.[dataKey]

    if (entry) {
      return [entry.data, entry.errorStatus]
    }

    return [null, null]
  }
)

export const selectKeyMetricRawDataFetching = (
  state: ApplicationState,
  id: string,
  body?: TKeyMetricDataBody
): boolean => {
  const dataKey = getKeyMetricDataKey(body)

  return state.KeyMetricStore.fetching[id]?.[dataKey] ?? false
}

export const selectAvailableKeyMetrics = createSelector(
  [selectKeyMetrics],
  ([keyMetrics]: [TKeyMetric[], boolean]) => {
    const toEntries = (km: TKeyMetric): [string, TKeyMetric][] => {
      return [[km.id, km], ...km.children.flatMap(toEntries)]
    }

    const allKeyMetrics = keyMetrics.flatMap(toEntries)

    return Object.fromEntries(allKeyMetrics) as Record<string, TKeyMetric>
  }
)
